import { useState } from 'react';
import { Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { trocarCliente } from '@/lib/api';
import { translateError } from '@/lib/errors';
import { useRotulos } from '@/i18n/rotulos';
import { toast } from '@/lib/toast';
import { useAuthStore } from '@/stores/authStore';

/**
 * Cliente ativo da sessão. Com um só vínculo mostra o nome; com vários, vira seletor.
 *
 * A troca recarrega o app em /app em vez de só invalidar o cache: papel, menu, hubs SignalR
 * (conectados com o token do cliente anterior) e toda query em memória mudam de uma vez. O
 * bootstrap renova com o refresh token novo, que já pertence ao cliente escolhido.
 */
export function ClienteSwitcher() {
  const { t } = useTranslation('app');
  const rotulos = useRotulos();
  const user = useAuthStore((s) => s.user);
  const [trocando, setTrocando] = useState(false);

  if (!user || user.vinculos.length === 0) return null;

  const ativo = user.vinculos.find((v) => v.hospitalId === user.hospitalId);

  if (user.vinculos.length === 1) {
    return (
      <span className="hidden items-center gap-1.5 text-sm text-vyxora-text-light/70 sm:flex">
        <Building2 className="h-4 w-4" /> {ativo?.nome ?? user.vinculos[0].nome}
      </span>
    );
  }

  async function trocar(hospitalId: string) {
    const rt = useAuthStore.getState().refreshToken;
    if (!rt || hospitalId === user?.hospitalId) return;
    setTrocando(true);
    try {
      const tokens = await trocarCliente(hospitalId, rt);
      useAuthStore.getState().setTokens(tokens);
      window.location.assign('/app');
    } catch (e) {
      toast.error(translateError(e));
      setTrocando(false);
    }
  }

  return (
    <label className="flex items-center gap-1.5 text-sm">
      <Building2 className="h-4 w-4 text-vyxora-text-light/60" aria-hidden />
      <span className="sr-only">{t('clienteSwitcher.clienteAtivo')}</span>
      <select
        value={user.hospitalId ?? ''}
        disabled={trocando}
        onChange={(e) => trocar(e.target.value)}
        className="max-w-[16rem] rounded border border-white/10 bg-white/5 px-2 py-1 text-vyxora-text-light outline-none focus:border-vyxora-cyan-400 disabled:opacity-50"
      >
        {user.vinculos.map((v) => (
          <option key={v.hospitalId} value={v.hospitalId} className="bg-vyxora-bg-dark">
            {v.nome} — {rotulos.tipoCliente(v.tipo)} · {rotulos.papel(v.papel)}
          </option>
        ))}
      </select>
    </label>
  );
}
